"use client";

import { LoadMoreButton } from "@/components/load-more-button";
import EmptyState from "@/components/portfolio/empty-state";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useEffect, useMemo, useRef, useState } from "react";
import CommentInput from "../comments/comment-input";
import CommentItem from "../comments/comment-item";
import { useCommentsTab, useLoadMoreReplies } from "../comments/hooks";
import ReplyDialog from "../comments/reply-dialog";
import { useCommentsStore } from "../comments/store";

interface CommentsTabProps {
    eventNo: string;
    commentId?: string;
    eventData?: { eventImageUrl?: string; eventTitle: string; position?: "Yes" | "No" | "No Position" };
    matchups?: any[];
}

export default function CommentsTab( { eventNo, commentId, eventData, matchups }: CommentsTabProps ) {
    const {
        comments,
        replyDialogOpen,
        targetComment,
        handleReplySubmit,
        handleDialogOpenChange,
        loading,
        initialized,
        rootCommentsQuery,
        replying,
    } = useCommentsTab( eventNo, commentId );

    const replyPagination = useCommentsStore( ( state ) => state.replyPagination );
    const { loadMoreReplies } = useLoadMoreReplies( eventNo );
    const [ loadingReplies, setLoadingReplies ] = useState<Record<number, boolean>>( {} );
    const [ expandedRoots, setExpandedRoots ] = useState<Record<number, boolean>>( {} );
    const hasAutoScrolledRef = useRef( false );

    const showEmptyState = initialized && !loading && comments.length === 0;

    const handleLoadMoreReplies = async ( rootId: string, rootServerId: number ) => {
        setLoadingReplies( ( prev ) => ( { ...prev, [rootServerId]: true } ) );
        try {
            await loadMoreReplies( rootId );
        } finally {
            setLoadingReplies( ( prev ) => ( { ...prev, [rootServerId]: false } ) );
        }
    };

    useEffect( () => {
        if ( comments.length === 0 ) {
            return;
        }
        setExpandedRoots( ( prev ) => {
            let changed = false;
            const next = { ...prev };
            comments.forEach( ( comment ) => {
                if ( comment.serverId && next[comment.serverId] === undefined ) {
                    next[comment.serverId] = true;
                    changed = true;
                }
            } );
            return changed ? next : prev;
        } );
    }, [ comments ] );

    useEffect( () => {
        if ( !commentId || hasAutoScrolledRef.current ) {
            return;
        }
        if ( !initialized || loading || comments.length === 0 ) {
            return;
        }

        let targetServerId: number | null = null;
        for ( const comment of comments ) {
            if ( comment.highlight ) {
                targetServerId = comment.serverId;
                break;
            }
            const highlightReply = ( comment.replies ?? [] ).find( ( reply ) => Boolean( reply.highlight ) );
            if ( highlightReply ) {
                targetServerId = highlightReply.serverId;
                break;
            }
        }
        if ( !targetServerId ) {
            return;
        }
        const element = document.getElementById( `comment-anchor-${targetServerId}` );
        if ( !element ) {
            return;
        }
        hasAutoScrolledRef.current = true;
        element.scrollIntoView( { behavior: "smooth", block: "center" } );
    }, [ commentId, comments, initialized, loading ] );

    const toggleRootReplies = ( serverId: number | undefined ) => {
        if ( !serverId ) return;
        setExpandedRoots( ( prev ) => ( { ...prev, [serverId]: !( prev[serverId] ?? true ) } ) );
    };

    const replyMeta = useMemo( () => {
        return comments.reduce<Record<number, { count: number; hasMore: boolean }>>( ( acc, comment ) => {
            if ( !comment.serverId ) {
                return acc;
            }
            const total = comment.comments ?? comment.replies?.length ?? 0;
            const hasMore = replyPagination[comment.serverId]?.hasMore ?? false;
            acc[comment.serverId] = { count: total, hasMore };
            return acc;
        }, {} );
    }, [ comments, replyPagination ] );

    return (
        <div>
            <CommentInput eventData={eventData} matchups={matchups} />

            {loading && comments.length === 0
                ? (
                    <div className="py-24 flex items-center justify-center">
                        <LoadingSpinner size="lg" />
                    </div>
                )
                : showEmptyState
                ? <EmptyState text="No comments yet" className="h-auto py-24" textClassName="text-sm" />
                : (
                    <div className="space-y-3 mt-3">
                        {comments.map( ( comment, index ) => {
                            const rootServerId = comment.serverId;
                            const isExpanded = rootServerId ? expandedRoots[rootServerId] ?? true : true;
                            const summary = rootServerId ? replyMeta[rootServerId] : undefined;
                            const totalReplies = summary?.count ?? comment.replies?.length ?? 0;
                            const hasMore = summary?.hasMore ?? false;
                            const paginationMeta = rootServerId ? replyPagination[rootServerId] : undefined;
                            const shouldRenderToggle = ( totalReplies > 0 || hasMore ) && Boolean( rootServerId );
                            const isLastComment = index === comments.length - 1;

                            const rootControls = shouldRenderToggle && rootServerId
                                ? {
                                    totalReplies,
                                    isExpanded,
                                    onToggle: () => toggleRootReplies( rootServerId ),
                                }
                                : undefined;

                            return (
                                <div
                                    key={comment.id}
                                    className={isLastComment ? undefined : "border-b border-stroke pb-3"}
                                >
                                    <CommentItem
                                        comment={comment}
                                        replyParentId={comment.id}
                                        rootControls={rootControls}
                                        matchups={matchups}
                                    />

                                    {isExpanded && comment.replies && comment.replies.length > 0 && (
                                        <div className="mt-3 flex flex-col gap-3">
                                            {comment.replies.map( ( reply ) => (
                                                <CommentItem
                                                    key={reply.id}
                                                    comment={reply}
                                                    replyParentId={comment.id}
                                                    isReply
                                                    matchups={matchups}
                                                />
                                            ) )}
                                        </div>
                                    )}

                                    {isExpanded && paginationMeta?.hasMore && rootServerId && (
                                        <div className="-my-8">
                                            <LoadMoreButton
                                                query={{
                                                    data: comment.replies ?? [],
                                                    hasMore: paginationMeta.hasMore ?? false,
                                                    isLoadingMore: loadingReplies[rootServerId] ?? false,
                                                    loadMore: () => handleLoadMoreReplies( comment.id, rootServerId ),
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        } )}
                    </div>
                )}

            <LoadMoreButton query={rootCommentsQuery} />

            {targetComment && eventData && (
                <ReplyDialog
                    open={replyDialogOpen}
                    onOpenChange={handleDialogOpenChange}
                    eventData={eventData}
                    targetComment={targetComment}
                    onReplySubmit={handleReplySubmit}
                    submitting={replying}
                    matchups={matchups}
                />
            )}
        </div>
    );
}
